import { useCallback, useRef, useState } from 'react';

export const uid = () => Math.random().toString(36).slice(2, 10);

export const createElement = (type, canvas, extra = {}) => {
  const base = { id: uid(), type, x: 40, y: 80, rotation: 0, opacity: 1, locked: false, hidden: false, groupId: null };
  if (type === 'text') {
    return { ...base, name: 'Text', w: canvas.width - 80, h: 60, text: 'Double-tap to edit', fontFamily: 'Unbounded', fontSize: 28, fontWeight: 600, fontStyle: 'normal', color: '#ffffff', align: 'left', lineHeight: 1.2, letterSpacing: 0, ...extra };
  }
  if (type === 'image') {
    return { ...base, name: 'Image', w: 200, h: 200, x: (canvas.width - 200) / 2, src: '', fit: 'cover', radius: 16, ...extra };
  }
  if (type === 'line') {
    return { ...base, name: 'Line', w: canvas.width - 80, h: 24, stroke: '#EAB308', strokeWidth: 3, dash: 'solid', cap: 'round', ...extra };
  }
  if (type === 'shape') {
    return { ...base, name: 'Shape', w: 160, h: 160, x: (canvas.width - 160) / 2, shape: 'star', fill: '#EAB308', stroke: '#ffffff', strokeWidth: 0, ...extra };
  }
  if (type === 'ellipse') {
    return { ...base, name: 'Circle', w: 160, h: 160, fill: '#EAB308', stroke: '#ffffff', strokeWidth: 0, ...extra };
  }
  return { ...base, name: 'Rectangle', w: canvas.width - 80, h: 120, fill: '#A855F7', stroke: '#ffffff', strokeWidth: 0, radius: 16, ...extra };
};

export const createPage = (elements = []) => ({ id: uid(), elements });

export const normalizePages = (menu) => {
  if (menu && Array.isArray(menu.pages) && menu.pages.length) {
    return menu.pages.map((p) => ({ id: p.id || uid(), elements: p.elements || [] }));
  }
  return [{ id: uid(), elements: (menu && menu.elements) || [] }];
};

export const firstPageElements = (menu) => normalizePages(menu)[0].elements;

export const useHistory = (initial) => {
  const [state, setState] = useState(initial);
  const past = useRef([]);
  const future = useRef([]);
  const [, bump] = useState(0);

  const commit = useCallback(() => {
    past.current = [...past.current.slice(-49), state];
    future.current = [];
    bump((n) => n + 1);
  }, [state]);

  const update = useCallback((updater, { record = true } = {}) => {
    setState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (record) {
        past.current = [...past.current.slice(-49), prev];
        future.current = [];
      }
      return next;
    });
  }, []);

  const undo = useCallback(() => {
    if (!past.current.length) return;
    setState((prev) => {
      const target = past.current[past.current.length - 1];
      past.current = past.current.slice(0, -1);
      future.current = [prev, ...future.current];
      return target;
    });
  }, []);

  const redo = useCallback(() => {
    if (!future.current.length) return;
    setState((prev) => {
      const [target, ...rest] = future.current;
      future.current = rest;
      past.current = [...past.current, prev];
      return target;
    });
  }, []);

  return { state, update, commit, undo, redo, canUndo: past.current.length > 0, canRedo: future.current.length > 0, reset: setState };
};
